import { getFileItemsFromCache } from 'owa-groups-services';
import { getFileItemsPromiseOverride } from 'owa-groups-adaptors';
import { isSuccess } from 'owa-groups-services';
import BaseMailboxFileItem from 'owa-service/lib/contract/BaseMailboxFileItem';
import GetFilesResponseType from 'owa-service/lib/contract/GetFilesResponseType';
import updateRecentFilesStore from 'owa-group-files-hub-store/lib/actions/updateRecentFilesStore';
import updateRecentFilesLoadingAndErrorStatus from 'owa-group-files-hub-store/lib/actions/updateRecentFilesLoadingStatus';
import { getRecentFilesList } from 'owa-group-files-hub-store/lib/selectors/getRecentFilesList';
import { clearFilesPreviewInformationAction } from 'owa-group-files-hub-store/lib/actions/clearFilesPreviewInformationAction';
import FileListLoadingStatus from 'owa-group-files-hub-store/lib/Schema/FileListLoadingStatus';
import GetFileItemsCacheStatus from 'owa-service/lib/contract/GetFileItemsCacheStatus';
import {
    FileInformationConverterForGetFileItems,
    MaxFiles,
} from '../utils/FileInformationConverterForGetFileItems';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import prefetchFilePreviewInformationAction from '../actions/PrefetchFilePreviewInformationAction';
import {
    isServiceFetchError,
    logResponseDataForOwaServerCalls,
    logErrorForEmptyOrNullResponse,
} from 'owa-server-call-logging/lib/owaServerCallLogging';
import { getErrorFromObject } from '../logging/genericInformationLogging';
import { getGroupMailboxExternalId } from 'owa-groups-shared-store';
import GetFileItemsResponseFailureType from 'owa-service/lib/contract/GetFileItemsResponseFailureType';

function parseGetFileItemsResponse(
    response: any,
    datapoint: PerformanceDatapoint,
    groupSmtpAddress: string
) {
    if (datapoint) {
        datapoint.addCheckmark('ParsingResponse');
    }

    const recentFilesList = getRecentFilesList(groupSmtpAddress);

    if (response && response.Body && isSuccess(response.Body)) {
        const fileList: BaseMailboxFileItem[] = response.Body.FileItems;
        const responseStatus: GetFilesResponseType = response.Body.ResponseStatusType;
        const failureReason: GetFileItemsResponseFailureType = response.Body.FailureReason;
        const cacheStatus: GetFileItemsCacheStatus = response.Body.CacheStatus;

        if (failureReason !== GetFileItemsResponseFailureType.None) {
            recentFilesList.fileListCacheErrorStatus = failureReason.toString();
        }

        // we want to continue showing the loading indicator if the below conditions are not satisfied. so do nothing.
        // If it is still in loading status, or refresh call has already failed then we proceed to update the recent files store with the parsed response,
        // else, if the refresh call has already finished successfully, then we want to skip this part
        if (
            cacheStatus != null &&
            cacheStatus.IsRetrievedFromCache &&
            (responseStatus == GetFilesResponseType.Success ||
                responseStatus == GetFilesResponseType.PartialSuccess) &&
            fileList != null &&
            fileList.length > 0 &&
            (recentFilesList.fileListRefreshLoadingStatus == FileListLoadingStatus.Loading ||
                recentFilesList.fileListRefreshLoadingStatus == FileListLoadingStatus.Failed)
        ) {
            // If cache retrieved time is invalid, we do not know how old is the cached information. Set it to be null.
            const fileRetrievedTime = cacheStatus.Retrieved
                ? new Date(cacheStatus.Retrieved)
                : null;
            recentFilesList.fileItems = new FileInformationConverterForGetFileItems(
                fileList,
                groupSmtpAddress,
                fileRetrievedTime
            ).getFileInformation();
            recentFilesList.filesHash = cacheStatus.Hash;
            if (responseStatus == GetFilesResponseType.Success) {
                recentFilesList.fileListCacheLoadingStatus = FileListLoadingStatus.Success;
            } else {
                recentFilesList.fileListCacheLoadingStatus = FileListLoadingStatus.PartialSuccess;
            }

            // Update recent files list map
            updateRecentFilesStore(groupSmtpAddress, recentFilesList);

            // prefetch file preview information asynchronously
            prefetchFilePreviewInformationAction(recentFilesList.fileItems);
        }
    } else {
        if (datapoint) {
            logErrorForEmptyOrNullResponse(datapoint, response);
            datapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(
                    'Error response from server encountered while trying to read the response'
                )
            );
        }

        recentFilesList.fileListCacheLoadingStatus = FileListLoadingStatus.Failed;
        recentFilesList.fileListCacheErrorStatus =
            'Error response from server encountered while trying to read the response';
        updateRecentFilesStore(groupSmtpAddress, recentFilesList);
    }
}

export function getRecentFilesFromCache(groupSmtpAddress: string) {
    const datapoint = new PerformanceDatapoint('GroupFilesHubGetFileItemsFromCacheActionDP');

    datapoint.addCustomProperty('groupExternalId', getGroupMailboxExternalId(groupSmtpAddress));

    // clear the PreviewInformation Store
    clearFilesPreviewInformationAction();

    if (getFileItemsPromiseOverride) {
        getFileItemsPromiseOverride(groupSmtpAddress)
            .then((response: any) => {
                parseGetFileItemsResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                updateRecentFilesLoadingAndErrorStatus(
                    groupSmtpAddress,
                    FileListLoadingStatus.Failed,
                    error.stack,
                    true
                );

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    } else {
        getFileItemsFromCache(groupSmtpAddress, MaxFiles)
            .then((response: any) => {
                parseGetFileItemsResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                if (isServiceFetchError(error)) {
                    logResponseDataForOwaServerCalls(datapoint, error.response);
                }

                updateRecentFilesLoadingAndErrorStatus(
                    groupSmtpAddress,
                    FileListLoadingStatus.Failed,
                    error.stack,
                    true
                );

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    }
}
